/**
 * Pipeline Track Component
 *
 * Displays the 7-stage pipeline as a connected horizontal track.
 * Shows stage icons connected by lines that change color based on progress.
 */

import { PipelineStageBadge } from './PipelineStageBadge';
import type { PipelineStage, StageStatus } from '../../types/pipeline';
import { PIPELINE_STAGES } from '../../types/pipeline';

interface PipelineTrackProps {
  stages: Record<PipelineStage, StageStatus>;
  selectedStage?: PipelineStage;
  onStageSelect?: (stage: PipelineStage) => void;
}

function getConnectorColor(
  prevStatus: StageStatus,
  nextStatus: StageStatus
): string {
  if (prevStatus === 'completed' && nextStatus === 'completed') {
    return 'bg-accent-green';
  }
  if (prevStatus === 'completed' && nextStatus === 'active') {
    return 'bg-accent-yellow';
  }
  return 'bg-border';
}

export function PipelineTrack({
  stages,
  selectedStage,
  onStageSelect,
}: PipelineTrackProps) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center justify-between min-w-fit px-4 py-2">
        {PIPELINE_STAGES.map((stage, index) => {
          const status = stages[stage];
          const isLast = index === PIPELINE_STAGES.length - 1;
          const nextStage = PIPELINE_STAGES[index + 1];
          const nextStatus = nextStage ? stages[nextStage] : 'pending';

          return (
            <div key={stage} className="flex items-center">
              <PipelineStageBadge
                stage={stage}
                status={status}
                isSelected={selectedStage === stage}
                onClick={() => onStageSelect?.(stage)}
              />

              {!isLast && (
                <div
                  className={`w-8 h-0.5 mx-1 transition-colors duration-300 ${getConnectorColor(
                    status,
                    nextStatus
                  )}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
